import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import paymentService from '../services/paymentService';
import { readPendingCheckout, clearPendingCheckout } from '../lib/pendingCheckout';
import { FUNNEL, trackFunnel } from '../lib/funnel';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [accessTier, setAccessTier] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    let mounted = true;

    const checkPaymentStatus = async () => {
      if (!isAuthenticated || !user) {
        setHasAccess(false);
        setAccessTier(null);
        setHasUsedTrial(false);
        setPaymentStatus(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const status = await paymentService.getPaymentStatus();
        const usedTrial = await paymentService.hasUsedTrial();

        if (mounted) {
          setHasAccess(status.hasPaid);
          setAccessTier(status.accessTier);
          setHasUsedTrial(usedTrial);
          setPaymentStatus(status);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (mounted) {
          setHasAccess(false);
          setAccessTier(null);
          setHasUsedTrial(false);
          setPaymentStatus(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    checkPaymentStatus();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');

    const paymentResult = urlParams.get('payment') || hashParams.get('payment') || urlParams.get('sub') || hashParams.get('sub');
    const sessionId = urlParams.get('session_id') || hashParams.get('session_id');
    const redirectTo = urlParams.get('redirect') || hashParams.get('redirect');

    if (paymentResult && sessionId) {
      handlePaymentRedirect(paymentResult, sessionId, redirectTo);
    }
  }, []);

  // After login, if the visitor came from a "Try for £1" CTA, jump them straight to Stripe.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    if (hasAccess && accessTier !== 'trial') return; // already a paying user
    const pending = readPendingCheckout();
    if (!pending?.planId) return;
    clearPendingCheckout();
    (async () => {
      try {
        const { url } = await paymentService.startCheckout(
          pending.planId,
          window.location.origin,
          pending.context || 'post-auth'
        );
        if (url) window.location.href = url;
      } catch (err) {
        console.error('Auto-resume checkout failed:', err);
      }
    })();
  }, [isAuthenticated, isLoading, hasAccess, accessTier]);

  const handlePaymentRedirect = async (paymentResult, sessionId, redirectTo) => {
    console.log('🔄 Handling payment redirect:', { paymentResult, sessionId, redirectTo });

    trackFunnel(FUNNEL.STRIPE_RETURNED, { result: paymentResult });

    if (paymentResult === 'success') {
      try {
        console.log('✅ Payment successful! Starting verification...');
        await new Promise((resolve) => setTimeout(resolve, 2000));

        let attempts = 0;
        let isVerified = false;

        while (attempts < 5 && !isVerified) {
          console.log(`🔍 Verification attempt ${attempts + 1}/5`);
          isVerified = await paymentService.verifyCheckoutSession(sessionId);
          if (!isVerified) {
            console.log(`⏳ Attempt ${attempts + 1} failed, waiting 2 seconds...`);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            attempts++;
          }
        }

        if (isVerified) {
          paymentService.updateStoredPaymentStatus('completed');
          const status = await paymentService.getPaymentStatus();
          const usedTrial = await paymentService.hasUsedTrial();
          setHasAccess(status.hasPaid);
          setAccessTier(status.accessTier);
          setHasUsedTrial(usedTrial);
          setPaymentStatus(status);
          trackFunnel(FUNNEL.PAYMENT_VERIFIED, { access_tier: status.accessTier });
          console.log('✅ Payment verified! Access granted. Tier:', status.accessTier);
        } else {
          console.warn('⚠️ Payment verification failed after retries');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
      }
    } else if (paymentResult === 'cancelled') {
      paymentService.updateStoredPaymentStatus('cancelled');
    }

    if (redirectTo) navigate(redirectTo);

    const url = new URL(window.location);
    url.searchParams.delete('payment');
    url.searchParams.delete('sub');
    url.searchParams.delete('session_id');
    url.searchParams.delete('redirect');

    if (url.hash.includes('?')) {
      const hashPart = url.hash.split('?')[0];
      url.hash = hashPart;
    }

    window.history.replaceState({}, document.title, url.toString());
  };

  /**
   * Starts checkout for a specific plan.
   * @param {('trial'|'monthly'|'lifetime')} planId
   * @param {string} context
   */
  const initiatePayment = async (planId = 'monthly', context = 'project-creation') => {
    try {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to make a payment');
      }

      // Back-compat: if a caller passes only a context string (legacy `initiatePayment('design-inspiration')`),
      // treat it as the monthly plan.
      let plan = planId;
      let ctx = context;
      if (typeof planId === 'string' && !['trial', 'monthly', 'lifetime'].includes(planId)) {
        ctx = planId;
        plan = 'monthly';
      }

      trackFunnel(FUNNEL.PLAN_SELECTED, { plan, context: ctx });
      const { url } = await paymentService.startCheckout(plan, window.location.origin, ctx);
      trackFunnel(FUNNEL.STRIPE_REDIRECT, { plan, context: ctx });
      window.location.href = url;
      return { success: true };
    } catch (error) {
      console.error('Error initiating payment:', error);
      throw error;
    }
  };

  const refreshPaymentStatus = async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const status = await paymentService.getPaymentStatus();
      const usedTrial = await paymentService.hasUsedTrial();
      setHasAccess(status.hasPaid);
      setAccessTier(status.accessTier);
      setHasUsedTrial(usedTrial);
      setPaymentStatus(status);
    } catch (error) {
      console.error('Error refreshing payment status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const consumeTrial = async () => {
    if (accessTier !== 'trial') return { success: false, skipped: true };
    const result = await paymentService.markTrialConsumed();
    if (result?.success !== false) {
      // Refresh local state so the next paywall reflects the consumed trial.
      await refreshPaymentStatus();
    }
    return result;
  };

  const value = {
    hasAccess,
    accessTier,
    hasUsedTrial,
    isLoading,
    paymentStatus,
    initiatePayment,
    refreshPaymentStatus,
    consumeTrial,
    isPaymentRequired: isAuthenticated && !hasAccess,
    debugCheckPayment: async () => {
      console.log('🐛 Manual payment status check triggered');
      const status = await paymentService.getPaymentStatus();
      setHasAccess(status.hasPaid);
      setAccessTier(status.accessTier);
      console.log('🐛 Manual check result:', status);
      return status.hasPaid;
    },
  };

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export default PaymentContext;
